import { CashFlowStatementV1 } from "../../grader/cash-flow-statement/v1.grader";
import { AnswerConfig } from "../../grader/grade-input";
import { ExtractInput } from "../extract-input";
import { ExtractOutput } from "../extract-output";
import { extractTable } from "../utils/extract-table";
import { extractValueAndConfigFromCell } from "../utils/extract-value-from-cell";
import { getAccountAndLevel } from "../utils/get-account-and-level";

/**
 * extractCashFlowStatementV1
 *
 * 현금흐름표 V1 형식의 테이블 데이터를 추출해
 * LMS 채점용 데이터(CashFlowStatementV1[])로 변환하는 함수.
 *
 * 📌 사용 목적:
 * - 학생이 입력한 현금흐름표 데이터를 행별로 구조화하여 채점 및 검증 가능하게 함
 *
 * @param extractInput - ExtractInput (div: HTML 문자열 또는 HTMLElement 포함)
 * @returns ExtractOutput<CashFlowStatementV1[]> (추출된 행별 데이터)
 */
export function extractCashFlowStatementV1(
  extractInput: ExtractInput,
): ExtractOutput<CashFlowStatementV1[]> {
  const { version, questionType, div } = extractInput;

  // 'table.cash-flow-statement' 선택자로 테이블 추출
  const table = extractTable(div, `table.cash-flow-statement`);
  if (!table) throw new Error("테이블을 만들어주세요.");

  const sliceStart = 2; // 0: caption, 1: header 행이므로 데이터는 2번 인덱스부터 시작
  const flatAccountSheets: CashFlowStatementV1[] = [];

  // 테이블의 모든 <tr> 요소 가져오기
  const trs = table.getElementsByTagName("tr");

  // 헤더를 제외한 데이터 행만 순회
  for (const tr of Array.from(trs).slice(sliceStart)) {
    const tds = tr.getElementsByTagName("td");

    // 해당 행에서 계정명(name)과 계정의 레벨(level)을 추출
    const [name, level] = getAccountAndLevel(tr);

    // 각 열의 값 추출 (input/select/textContent 모두 대응)
    const values: string[] = [];
    const answerConfigs: (AnswerConfig | undefined)[] = [];
    Array.from(tds)
      .slice(1) // 첫 번째 열(계정명)을 제외하고 값 열만 순회
      .forEach((td) => {
        const { value, answerConfig } = extractValueAndConfigFromCell(td);
        values.push(value);
        answerConfigs.push(answerConfig);
      });

    // 추출된 데이터를 CashFlowStatementV1 형태로 추가
    flatAccountSheets.push({
      id: tr.id,
      name,
      values,
      level,
      answerConfigs,
    });
  }

  // 최종적으로 ExtractOutput 형식으로 반환
  return {
    version, // 문제 버전
    questionType, // 문제 유형
    entryCount: flatAccountSheets.length, // 추출된 행 수
    answerSheet: flatAccountSheets, // 추출된 현금흐름표 데이터
  };
}
